import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { CalculationRunOptions } from '../calculation-run.options';
import { CalculationKeynameEnum } from '../../../enums/calculations/calculation-keyname.enum';
import {
  advancedFormGroup,
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import {
  IsBoolean,
  IsInt,
  IsNotEmpty,
  IsOptional,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import {
  SalaryContributionsOptionsInSalaryDividendOptions,
} from './salary-contributions/salary-contributions-options-in-salary-dividend.options';
import { type } from '@pristine-ts/data-mapping-common';
import { QppPensionOptionsInSalaryDividendOptions } from './qpp-pension/qpp-pension-options-in-salary-dividend.options';
import {
  CorporateIncomeTaxInSalaryDividendOptions,
} from './income-tax/corporate/corporate-income-tax-in-salary-dividend.options';
import {
  IncomeTaxPersonalInSalaryDividendOptions,
} from './income-tax/personal/income-tax-personal-in-salary-dividend.options';
import {
  SalaryDividendAlgorithmEnum,
} from '../../../enums/calculations/salary-dividend/salary-dividend-algorithm.enum';

@translationBaseKey('salary-dividend-options')
export class SalaryDividendOptions extends CalculationRunOptions {

  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.SalaryDividend;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quelle est l\'année?',
        en: 'What is the year?',
      },
      tooltip: {
        fr: 'L\'année fiscale pour laquelle les calculs sont effectués.',
        en: 'The tax year for which the calculations are performed.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: '2023',
        },
        id: 2023,
      },
      {
        label: {
          fr: '2024',
        },
        id: 2024,
      },
      {
        label: {
          fr: '2025',
        },
        id: 2025,
      },
    ],
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(2023, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(2025, { buildErrorMessage: ErrorMessageBuilder.build })
  year: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous maximiser le versement de dividendes déterminés?',
        en: 'Do you want to maximize the payment of eligible dividends?',
      },
      tooltip: {
        fr: 'Les dividendes admissibles sont un type de dividende versé par une société canadienne qui reçoit un traitement fiscal spécial pour les actionnaires résidents canadiens. Ce traitement spécial se traduit généralement par un taux d\'imposition effectif inférieur pour l\'actionnaire par rapport à d\'autres types de revenus.',
        en: 'Eligible dividends are a type of dividend paid by a Canadian corporation that receives special tax treatment for Canadian resident shareholders. This special treatment generally results in a lower effective tax rate for the shareholder compared to other types of income.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isEligibleDividendMaximized: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous prendre en compte l\'augmentation de la pension RRQ dans le calcul?',
        en: 'Do you want to take QPP pension increase into account in the wage-dividend analysis?',
      },
      tooltip: {
        fr: 'Indique si vous souhaitez prendre en compte l\'augmentation de la pension RRQ lors de l\'analyse entre le salaire et les dividendes.',
        en: 'Indicates whether you want to take QPP pension increase into account during the analysis between salary and dividends.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isQppPensionCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que le salaire de l\'actionnaire est assujetti à l\'assurance-emploi?',
        en: 'Is the shareHolder salary subject to employment insurance?',
      },
      tooltip: {
        fr: 'Au-dessus de 40% de détention des actions, l\'actionnaire n\'est pas éligible aux prestations d\'assurance-emploi.',
        en: 'Above a certain threshold of ownership (40%), the shareholder is not elligible for Employment Insurance benefits.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isShareHolderSubjectToEI: boolean = false;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Coût de la vie',
        en: 'Cost of living',
      },
      tooltip: {
        fr: 'Montant annuel correspondant au coût de la vie, c\'est-à-dire l\'argent que vous souhaitez avoir dans vos poches après impôts.Le coût de vie est la liquidité nette personnelle dont l\'entrepreneur a besoin pour l\'année. Le logiciel calculer le salaire et le dividende à verser à l\'entrepreneur pour obtenir ce coût de vie. À noter que seul les revenus de salaire et de dividende simulé par le logiciel sont pris en considération pour le coût. Par exemple, les autres revenus de l\'entrepreneur ou du conjoint ne sont pas considéré dans le coût de la vie.',
        en: 'Annual amount corresponding to the cost of living, i.e. the money you want to have in your pocket after taxes.The cost of living is the personal net liquidity that the entrepreneur needs for the year. The software calculates the salary and dividend to be paid to the entrepreneur to obtain this cost of living. Note that only the salary and dividend income simulated by the software are taken into consideration for the cost. For example, other income of the entrepreneur or spouse is not considered in the cost of living.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  costOfLiving: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Algorithme',
        en: 'Algorithm',
      },
      tooltip: {
        fr: 'L\'algorithme à utiliser pour le calcul. "Precis" est plus précis mais plus lent, "Approximation" est plus rapide mais moins précis.',
        en: 'The algorithm to use for the calculation. "Precise" is more accurate but slower, "Approximation" is faster but less accurate.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: 'Precis',
          en: 'Precise',
        },
        id: 'PRECISE',
      },
      {
        label: {
          fr: 'Approximation',
          en: 'Approximation',
        },
        id: 'APPROXIMATION',
      },
    ],
  })
  @IsOptional()
  algorithm?: SalaryDividendAlgorithmEnum = SalaryDividendAlgorithmEnum.Precise;

  @type(() => new IncomeTaxPersonalInSalaryDividendOptions())
  @advancedFormGroup({
    classType: IncomeTaxPersonalInSalaryDividendOptions,
  })
  @ValidateNested()
  incomeTaxPersonalOptions: IncomeTaxPersonalInSalaryDividendOptions = new IncomeTaxPersonalInSalaryDividendOptions();

  @type(() => new SalaryContributionsOptionsInSalaryDividendOptions())
  @advancedFormGroup({
    classType: SalaryContributionsOptionsInSalaryDividendOptions,
  })
  @ValidateNested()
  salaryContributionsOptions: SalaryContributionsOptionsInSalaryDividendOptions = new SalaryContributionsOptionsInSalaryDividendOptions();

  @type(() => new QppPensionOptionsInSalaryDividendOptions())
  @advancedFormGroup({
    classType: QppPensionOptionsInSalaryDividendOptions,
  })
  @ValidateNested()
  @ValidateIf(object => object.isQppPensionCalculated === true)
  qppPensionOptions?: QppPensionOptionsInSalaryDividendOptions;

  @type(() => new CorporateIncomeTaxInSalaryDividendOptions())
  @advancedFormGroup({
    classType: CorporateIncomeTaxInSalaryDividendOptions,
  })
  @ValidateNested()
  corporateIncomeTaxOptions: CorporateIncomeTaxInSalaryDividendOptions = new CorporateIncomeTaxInSalaryDividendOptions();
}
